import React, { type ReactNode } from 'react';
import { styled } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { useUserSubscriber } from '@atlassian/jira-user-services/src/main.tsx';
import { VIEW_MODE, EDIT_MODE, COLLAPSED_MODE } from '../../common/constants';
import { DEFAULT_TIMEZONE } from '../../common/ui/constants';
import { CalendarView } from './calendar';
import { CalendarForm } from './editable';
import { Summary } from './summary-with-actions';
import type { CalendarWithSummaryProps, CalendarListProps } from './types';

export const CalendarWithSummary = ({
	calendar,
	permissionsWithTimezone,
	isSelected,
	selectedCalendarMode,
	calendarsActions,
}: CalendarWithSummaryProps) => {
	const { id } = calendar;
	const {
		onToggleViewRequested,
		onEditRequested,
		onEditCalendarSuccessful,
		onDeleteCalendarSuccessful,
		onReset,
		onCloneCalendar,
	} = calendarsActions;
	const mode = isSelected ? selectedCalendarMode : COLLAPSED_MODE;
	const [{ data: loggedInUser }] = useUserSubscriber();

	const userTimezone: string = (loggedInUser && loggedInUser.timeZone) ?? DEFAULT_TIMEZONE;

	const dataTestId = `servicedesk-sla-settings.ui.calendars.calendar-${id}`;
	switch (mode) {
		case VIEW_MODE: {
			return (
				<CalendarContainer data-test-id={dataTestId}>
					<Summary
						calendar={calendar}
						onEditRequested={() => onEditRequested(id)}
						onToggleDetails={() => onToggleViewRequested(id)}
						onDeleteCalendarSuccessful={onDeleteCalendarSuccessful}
						onCloneCalendar={onCloneCalendar}
						canCloneCalendar={Boolean(permissionsWithTimezone?.canCreate)}
						userTimeZone={userTimezone}
						isOpen
					/>
					<CalendarView calendar={calendar} permissionsWithTimezone={permissionsWithTimezone} />
				</CalendarContainer>
			);
		}

		case EDIT_MODE: {
			return (
				<CalendarContainer data-test-id={dataTestId}>
					<CalendarForm
						allTimezones={permissionsWithTimezone.allTimezones}
						defaultTimezone={permissionsWithTimezone.defaultTimezone}
						calendarData={calendar}
						onCancel={onReset}
						onEditCalendarSuccessful={onEditCalendarSuccessful}
						calendarMode={mode}
					/>
				</CalendarContainer>
			);
		}

		default: {
			return (
				<CalendarContainer data-test-id={dataTestId}>
					<Summary
						calendar={calendar}
						onEditRequested={() => onEditRequested(id)}
						onToggleDetails={() => onToggleViewRequested(id)}
						onDeleteCalendarSuccessful={onDeleteCalendarSuccessful}
						onCloneCalendar={onCloneCalendar}
						canCloneCalendar={Boolean(permissionsWithTimezone?.canCreate)}
						userTimeZone={userTimezone}
						isOpen={false}
					/>
				</CalendarContainer>
			);
		}
	}
};

export const CalendarList = ({
	calendars,
	permissionsWithTimezone,
	selectedCalendarMode,
	calendarsActions,
	selectedCalendarId,
}: CalendarListProps) => (
	<>
		{calendars.reduce<ReactNode[]>((calendarList, calender, calenderKey) => {
			calendarList.push(
				<CalendarWithSummary
					key={calenderKey}
					calendar={calender}
					selectedCalendarMode={selectedCalendarMode}
					isSelected={selectedCalendarId === calender.id}
					permissionsWithTimezone={permissionsWithTimezone}
					calendarsActions={calendarsActions}
				/>,
			);

			return calendarList;
		}, [])}
	</>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CalendarContainer = styled.div({
	borderRadius: '3px',
	border: `1px solid ${token('color.border', 'rgba(9, 30, 66, 0.08)')}`,
	boxSizing: 'border-box',
	margin: `${token('space.100', '8px')} 0`,
});
